import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { Channel } from "@shared/domain";

/*
 * The half-filled quick-entry booking. Persisted on the device, so a
 * session timeout or a closed tab never loses it.
 */
export type DraftAddon = { addonId: string; quantity: number };

export type Draft = {
  propertyId: string | null;
  /** The room type being sold; the room or beds follow from it. */
  roomTypeId: string | null;
  roomId: string | null;
  bedIds: string[];
  bedsCount: number;
  checkIn: string | null;
  checkOut: string | null;
  guestName: string;
  guestPhone: string;
  guestGender: string | null;
  channel: Channel | null;
  adults: number;
  children: number;
  /** Names of adults 2..n — the lead adult is guestName. One slot per head. */
  adultNames: string[];
  /** Names of the children, one slot each. */
  childNames: string[];
  mealPlanId: string | null;
  addons: DraftAddon[];
  /** The host looking after them, typed in. */
  pointOfContact: string;
  amount: number | null;
  notes: string;
  /** Quick: dates, room, who — the rest later. Full: every detail, one short step at a time. */
  mode: "quick" | "full";
  /** 1-based, within the mode's own list of steps. */
  step: number;
  startedAt: number | null;
  /** The source this desk last saved a booking under: a new booking starts on it, visibly. */
  lastChannel: Channel | null;
};

const empty: Draft = {
  propertyId: null,
  roomTypeId: null,
  roomId: null,
  bedIds: [],
  bedsCount: 1,
  checkIn: null,
  checkOut: null,
  guestName: "",
  guestPhone: "",
  guestGender: null,
  channel: null,
  adults: 2,
  children: 0,
  adultNames: [""],
  childNames: [],
  mealPlanId: null,
  addons: [],
  pointOfContact: "",
  amount: null,
  notes: "",
  mode: "quick",
  step: 1,
  startedAt: null,
  lastChannel: null,
};

/** A new booking's source until someone picks another — shown, never assumed silently. */
export const DEFAULT_CHANNEL: Channel = "walkin";

type DraftState = Draft & {
  patch: (p: Partial<Draft>) => void;
  start: (p: Partial<Draft>) => void;
  /** After a save: clear the booking, remember its source. */
  reset: (savedChannel?: Channel | null) => void;
};

export const useDraft = create<DraftState>()(
  persist(
    (set) => ({
      ...empty,
      patch: (p) => set(p),
      // A new booking keeps the way this desk likes to take them (quick or full)
      // and starts on the source it last used.
      start: (p) => set((s) => ({ ...empty, mode: s.mode, lastChannel: s.lastChannel, channel: s.lastChannel ?? DEFAULT_CHANNEL, ...p, startedAt: Date.now() })),
      reset: (savedChannel) => set((s) => ({ ...empty, mode: s.mode, lastChannel: savedChannel ?? s.lastChannel })),
    }),
    { name: "kk-draft" },
  ),
);

export const draftHasContent = (d: Draft) => !!(d.guestName.trim() || d.guestPhone.trim() || d.roomId);

/** Grow or trim a list of names to `n` slots, keeping what was already typed. */
export const resize = (names: string[], n: number) => (names.length === n ? names : Array.from({ length: Math.max(0, n) }, (_, i) => names[i] ?? ""));

/** The lead guest plus every other head, in the order the server stores them. */
export const occupantsOf = (d: Draft) => [
  { kind: "adult" as const, name: d.guestName },
  ...resize(d.adultNames, Math.max(0, d.adults - 1)).map((name) => ({ kind: "adult" as const, name })),
  ...resize(d.childNames, d.children).map((name) => ({ kind: "child" as const, name })),
];
